import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { employeesQueryKey, listEmployees } from '@/lib/api/employees';
import {
  bulkAssignJobCrew,
  bulkSetJobCrewPay,
  bulkUnassignJobCrew,
  type JobCrewScope,
  type SeriesEmployeePay,
} from '@/lib/api/jobs';

import { CrewEmployeeRow } from './crew-employee-row';
import { crewResultMessage } from './helpers';
import { ScopeSelect } from './scope-select';
import { addCrewSchema, type AddCrewInput } from './schema';
import type { CrewBulkAction } from './types';

interface Props {
  businessId: string;
  jobId: string;
  roster: SeriesEmployeePay[];
}

const DEFAULT_ADD: AddCrewInput = {
  employeeId: '',
  payAmount: '',
  scope: 'upcoming',
};

export function SeriesCrewBar({ businessId, jobId, roster }: Props) {
  const { t } = useLanguage();
  const [adding, setAdding] = useState(false);

  const employeesQuery = useQuery({
    queryKey: employeesQueryKey(businessId, 'active'),
    queryFn: () => listEmployees(businessId, 'active'),
  });

  const nameFor = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of employeesQuery.data ?? []) map.set(e.id, e.full_name);
    for (const r of roster) map.set(r.employee_id, r.full_name);
    return (id: string) => map.get(id) ?? '';
  }, [employeesQuery.data, roster]);

  const addForm = useForm<AddCrewInput>({
    defaultValues: DEFAULT_ADD,
    resolver: zodResolver(addCrewSchema),
  });

  function reportResult(action: CrewBulkAction, employeeId: string) {
    return (result: Parameters<typeof crewResultMessage>[0]) => {
      const { tone, text } = crewResultMessage(
        result,
        action,
        nameFor(employeeId),
        t,
      );
      if (tone === 'success') toast.success(text);
      else toast.message(text);
    };
  }

  const addMutation = useMutation({
    mutationFn: (input: AddCrewInput) =>
      bulkAssignJobCrew(businessId, jobId, {
        employee_id: input.employeeId,
        pay_amount: input.payAmount ? Number(input.payAmount) : undefined,
        scope: input.scope,
      }),
    onSuccess: (result, input) => {
      reportResult('add', input.employeeId)(result);
      addForm.reset(DEFAULT_ADD);
      setAdding(false);
    },
  });

  const changePayMutation = useMutation({
    mutationFn: (vars: {
      employeeId: string;
      payAmount: number;
      scope: JobCrewScope;
    }) =>
      bulkSetJobCrewPay(businessId, jobId, {
        employee_id: vars.employeeId,
        pay_amount: vars.payAmount,
        scope: vars.scope,
      }),
    onSuccess: (result, vars) =>
      reportResult('change-pay', vars.employeeId)(result),
  });

  const removeMutation = useMutation({
    mutationFn: (vars: { employeeId: string; scope: JobCrewScope }) =>
      bulkUnassignJobCrew(businessId, jobId, {
        employee_id: vars.employeeId,
        scope: vars.scope,
      }),
    onSuccess: (result, vars) => reportResult('remove', vars.employeeId)(result),
  });

  const busy =
    addMutation.isPending ||
    changePayMutation.isPending ||
    removeMutation.isPending;

  const employees = employeesQuery.data ?? [];

  return (
    <section className="flex flex-col gap-3 rounded-xl border bg-card p-4" data-testid="series-crew-bar">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">{t.seriesCrew}</h2>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => setAdding((v) => !v)}
          data-testid="series-add-crew"
        >
          {t.seriesAddCrew}
        </Button>
      </div>

      {adding && (
        <form
          onSubmit={addForm.handleSubmit((data) => addMutation.mutate(data))}
          className="flex flex-wrap items-end gap-2 rounded-md border bg-muted/30 p-2"
        >
          <Controller
            control={addForm.control}
            name="employeeId"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="series-crew-employee">{t.seriesCrewEmployee}</Label>
                <select
                  {...field}
                  id="series-crew-employee"
                  data-testid="series-crew-employee"
                  className="px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">{t.seriesCrewSelectEmployee}</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.full_name}
                    </option>
                  ))}
                </select>
                {fieldState.error && (
                  <p className="text-xs text-destructive">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
          <Controller
            control={addForm.control}
            name="payAmount"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="series-crew-pay">{t.jobPayAmount}</Label>
                <Input
                  {...field}
                  id="series-crew-pay"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  className="w-24"
                  data-testid="series-crew-pay"
                />
                {fieldState.error && (
                  <p className="text-xs text-destructive">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
          <Controller
            control={addForm.control}
            name="scope"
            render={({ field }) => (
              <ScopeSelect
                id="series-crew-add-scope"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Button
            type="submit"
            size="sm"
            disabled={busy}
            data-testid="series-crew-add-submit"
          >
            {t.seriesAddCrew}
          </Button>
        </form>
      )}

      {roster.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t.seriesCrewNoAssignments}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {roster.map((row) => (
            <CrewEmployeeRow
              key={row.employee_id}
              row={row}
              busy={busy}
              onChangePay={(payAmount, scope) =>
                changePayMutation.mutate({
                  employeeId: row.employee_id,
                  payAmount,
                  scope,
                })
              }
              onRemove={(scope) =>
                removeMutation.mutate({ employeeId: row.employee_id, scope })
              }
            />
          ))}
        </ul>
      )}
    </section>
  );
}
